import React from 'react';
import { Alert, Button, Card, Form, Input, Modal, Select, Space, Switch, Table, Tabs } from 'antd';
import { MailOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { FormInstance } from 'antd/es/form';
import type { CompanyMember, Invitation, Role } from './types';

const { TabPane } = Tabs;
const { Option } = Select;

interface UserManagementTabProps {
  isAdmin: boolean;
  isMaster: boolean;
  companyMembers: CompanyMember[];
  invitations: Invitation[];
  userColumns: ColumnsType<CompanyMember>;
  invitationColumns: ColumnsType<Invitation>;
  inviteModalVisible: boolean;
  setInviteModalVisible: (visible: boolean) => void;
  inviteForm: FormInstance;
  handleInviteUser: (values: any) => void;
  roles: Role[];
  loading: boolean;
  getRoleIcon: (code: string) => React.ReactNode;
}

const UserManagementTab: React.FC<UserManagementTabProps> = ({
  isAdmin,
  isMaster,
  companyMembers,
  invitations,
  userColumns,
  invitationColumns,
  inviteModalVisible,
  setInviteModalVisible,
  inviteForm,
  handleInviteUser,
  roles,
  loading,
  getRoleIcon,
}) => {
  if (!isAdmin) {
    return (
      <Alert
        message="Access Restricted"
        description="Only company administrators can access user management features."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        className="configuration-users-card"
        title={
          <Space>
            <TeamOutlined />
            User Management
          </Space>
        }
        extra={
          <Button
            className="configuration-users-invite-button"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setInviteModalVisible(true)}
          >
            Invite User
          </Button>
        }
      >
        <Tabs defaultActiveKey="users" className="configuration-users-tabs">
          <TabPane tab="Company Users" key="users">
            <Table
              className="configuration-users-table"
              columns={userColumns}
              dataSource={companyMembers}
              rowKey="membershipId"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          <TabPane tab="Pending Invitations" key="invitations">
            <Table
              className="configuration-invitations-table"
              columns={invitationColumns}
              dataSource={invitations}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title="Invite User to Company"
        open={inviteModalVisible}
        onCancel={() => setInviteModalVisible(false)}
        footer={null}
      >
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={handleInviteUser}
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="user@example.com"
            />
          </Form.Item>

          <Form.Item
            name="roleId"
            label="Role"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select placeholder="Select a role">
              {roles.map((role) => (
                <Option key={role.id} value={role.id}>
                  <Space>
                    {getRoleIcon(role.code)}
                    {role.description}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {isMaster && (
            <Form.Item
              name="isMaster"
              label="Master Access"
              valuePropName="checked"
              extra="Master access grants system-wide permissions beyond company roles."
            >
              <Switch />
            </Form.Item>
          )}

          <Form.Item
            name="message"
            label="Personal Message (Optional)"
          >
            <Input.TextArea
              placeholder="Add a personal message to the invitation..."
              rows={3}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Send Invitation
              </Button>
              <Button onClick={() => setInviteModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default UserManagementTab;
